import {KeyData, KeyHistoryRecord} from "./Model";
import {create} from "zustand";
import {derivedRemoteStore} from "./DerivedStore";
import {useKeyDataStore} from "./KeyDataStore";
import HistoryToggleOffIcon from "@mui/icons-material/HistoryToggleOff";
import {OverridableComponent} from "@mui/material/OverridableComponent";
import AlarmOnIcon from "@mui/icons-material/AlarmOn";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import OverlapIcon from "@mui/icons-material/LowPriority";
import GapIcon from "@mui/icons-material/Compress";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import {SvgIconTypeMap} from "@mui/material";
import {toLocationTypeEnum} from "../components/location/KeyLocationShield";
import {today} from "../util/DateUtil";


export type KeyProblem =
    'all'
    | 'no-active-record'
    | 'multiple-active-record'
    | 'not-returned-key'
    | 'to-before-from'
    | 'history-gap'
    | 'history-overlap';
export type KeyProblemOption = { value: KeyProblem, label: string, icon?: OverridableComponent<SvgIconTypeMap> };

export class KeyProblems {
    static readonly problems: Record<keyof any, KeyProblemOption> = {
        all: {value: 'all', label: 'All Keys'},
        noActiveRecord: {value: 'no-active-record', label: 'No Active Record', icon: HistoryToggleOffIcon},
        multipleActiveRecord: {
            value: 'multiple-active-record',
            label: 'Multiple Active Records',
            icon: ContentCopyIcon
        },
        notReturnedKey: {value: 'not-returned-key', label: 'Not Returned Key', icon: AlarmOnIcon},
        toBeforeFrom: {value: 'to-before-from', label: 'History Record Ends Before Starts', icon: EventBusyIcon},
        historyGap: {value: 'history-gap', label: 'Gap in Key History', icon: GapIcon},
        historyOverlap: {value: 'history-overlap', label: 'History Overlap', icon: OverlapIcon},
    };

    static readonly options = Object.values(KeyProblems.problems)
        .map(option => option);

    private readonly problems = new Map<KeyData['id'], Record<KeyProblem, KeyHistoryRecord['id'][] | undefined>>();

    constructor(keyData: KeyData[]) {
        keyData.forEach(key => {
            const problems = KeyProblems.collectProblems(key);
            if (problems) {
                this.problems.set(key.id, problems)
            }
        });
    }

    getKeyProblem(keyData: KeyData): KeyProblemOption | undefined {
        const problems = this.problems.get(keyData.id);
        if (!problems) return undefined;
        return KeyProblems.options.find(option => problems[option.value]);
    }

    getRecordProblem(keyData: KeyData, record: KeyHistoryRecord): KeyProblemOption | undefined {
        const problems = this.problems.get(keyData.id);
        if (!problems) return undefined;
        return KeyProblems.options.find(option => problems[option.value] && problems[option.value]?.includes(record.id));
    }

    hasProblem(keyData: KeyData, problem: KeyProblem): boolean {
        return this.problems.get(keyData.id)?.[problem] !== undefined;
    }

    private static collectProblems(keyData: KeyData): Record<KeyProblem, KeyHistoryRecord['id'][] | undefined> | undefined {
        let problems: Record<KeyProblem, KeyHistoryRecord['id'][] | undefined> = {
            'all': undefined,
            'no-active-record': undefined,
            'multiple-active-record': undefined,
            'not-returned-key': undefined,
            'to-before-from': undefined,
            'history-gap': undefined,
            'history-overlap': undefined,
        };

        const activeRecords = keyData.history.filter(record => record.to == null);

        if (activeRecords.length === 0) {
            problems['no-active-record'] = [];
        }

        if (activeRecords.length > 1) {
            problems['multiple-active-record'] = activeRecords.map(record => record.id);
        }

        const expiredActiveRecords = activeRecords
            .filter(record => {
                const expTo = toLocationTypeEnum(record.location).expectedReturnDate
                return expTo && expTo < today();
            })
            .map(record => record.id);
        if (expiredActiveRecords.length > 0) {
            problems['not-returned-key'] = expiredActiveRecords;
        }

        const recordsWithToBeforeFrom = keyData.history
            .filter(record => record.from && record.to && record.to < record.from)
            .map(record => record.id);
        if (recordsWithToBeforeFrom.length > 0) {
            problems['to-before-from'] = recordsWithToBeforeFrom;
        }

        for (let i = 0; i < keyData.history.length - 1; i++) {
            const prev = keyData.history[i];
            const next = keyData.history[i + 1];
            if (!prev.to || !next.from) continue;

            if (prev.to > next.from) {
                problems['history-overlap'] = problems['history-overlap'] || [];
                problems['history-overlap'].push(next.id);
            } else {
                const prevTo = new Date(prev.to);
                const nextFrom = new Date(next.from);
                // 26 hours to work around the leap day issue
                if (nextFrom.getTime() - prevTo.getTime() > 26 * 60 * 60 * 1000) {
                    problems['history-gap'] = problems['history-gap'] || [];
                    problems['history-gap'].push(next.id);
                }
            }
        }

        return problems;
    }
}

export const useKeyProblemStore = create(
    derivedRemoteStore(
        {keyData: useKeyDataStore},
        ({keyData}) => new KeyProblems(keyData),
    )
);
